/**
 * Section words in every language the catalog ships, ported from jnana-native's
 * `wordsByLanguageCodes` (jnana-native/src/types/languages.ts).
 *
 * Why this exists: the web fetches lyrics in the reader's own language
 * (`BhajanDetails` passes `language` to `fetchLyrics`), but the heading regex used
 * to match Latin/IAST spellings only. In the other nine scripts nothing matched, so
 * the whole document collapsed into one 'other' stanza — headings rendered as
 * ordinary lyric lines and Repeat Pallavi silently did nothing at all.
 *
 * Keep this table in step with native's.
 */
export interface SectionWords {
  pallavi: string;
  anupallavi: string;
  caranam: string;
  slokam: string;
}

const SECTION_WORDS_BY_LANGUAGE: Record<string, SectionWords> = {
  en: { pallavi: 'pallavi', anupallavi: 'anupallavi', caranam: 'caraṇaṁ', slokam: 'ślōkaṁ' },
  te: { pallavi: 'పల్లవి', anupallavi: 'అనుపల్లవి', caranam: 'చరణం', slokam: 'శ్లోకం' },
  kn: { pallavi: 'ಪಲ್ಲವಿ', anupallavi: 'ಅನುಪಲ್ಲವಿ', caranam: 'ಚರಣಂ', slokam: 'ಶ್ಲೋಕಂ' },
  hi: { pallavi: 'पल्लवि', anupallavi: 'अनुपल्लवि', caranam: 'चरणं', slokam: 'श्लोकं' },
  ta: { pallavi: 'பல்லவி', anupallavi: 'அனுபல்லவி', caranam: 'சரணம்', slokam: 'ஶ்லோகம்' },
  gu: { pallavi: 'પલ્લવિ', anupallavi: 'અનુપલ્લવિ', caranam: 'ચરણં', slokam: 'શ્લોકં' },
  bn: { pallavi: 'পল্লবি', anupallavi: 'অনুপল্লবি', caranam: 'চরণং', slokam: 'শ্লোকং' },
  ml: { pallavi: 'പല്ലവി', anupallavi: 'അനുപല്ലവി', caranam: 'ചരണം', slokam: 'ശ്ലോകം' },
  or: { pallavi: 'ପଲ୍ଲଵି', anupallavi: 'ଅନୁପଲ୍ଲଵି', caranam: 'ଚରଣଂ', slokam: 'ଶ୍ଲୋକଂ' },
  pa: { pallavi: 'ਪਲ੍ਲਵਿ', anupallavi: 'ਅਨੁਪਲ੍ਲਵਿ', caranam: 'ਚਰਣੰ', slokam: 'ਸ਼੍ਲੋਕੰ' },
};

export function sectionWordsForLanguage(languageCode: string): SectionWords {
  return SECTION_WORDS_BY_LANGUAGE[languageCode] ?? SECTION_WORDS_BY_LANGUAGE.en;
}

/** Escape a word for literal use inside a regex. */
function escape(word: string): string {
  return word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * The Latin/IAST spellings the catalog uses regardless of the reader's language.
 * Transliterated headings appear in real data, so these are ALWAYS in the
 * alternation alongside the language's own script — dropping them would regress
 * every English bhajan.
 *
 * These are regex fragments, not literals, so they are never passed through
 * `escape`.
 */
const ALWAYS_LATIN = [
  'pallavi',
  'anupallavi',
  'caraṇa[mṁ]?',
  'charanam',
  'caranam',
  'samasti\\s+caraṇa[mṁ]?',
  'chittasvara',
  'kalpanasvara',
  'śloka',
  'sloka',
  'maṅgaḷam',
  'mangalam',
  'svaram',
  'svara',
];

/**
 * Longest-first, always. `pallavi` is a substring of `anupallavi` in every script,
 * so a shortest-first alternation would classify every anupallavi as a pallavi.
 */
function longestFirst(parts: string[]): string {
  return Array.from(new Set(parts))
    .sort((a, b) => b.length - a.length)
    .join('|');
}

function alternation(languageCode: string): string {
  const words = sectionWordsForLanguage(languageCode);
  const localised = [words.pallavi, words.anupallavi, words.caranam, words.slokam].map(escape);
  return longestFirst([...localised, ...ALWAYS_LATIN]);
}

/**
 * Matches a whole heading line — anchored at both ends, so a lyric that merely
 * contains the word "pallavi" is never mistaken for a section heading.
 */
export function buildSectionRegex(languageCode: string): RegExp {
  return new RegExp(`^\\s*(${alternation(languageCode)})\\s*[:：]?\\s*$`, 'i');
}

function buildOneWordRegex(word: string, extraLatin: string[]): RegExp {
  return new RegExp(`^\\s*(${longestFirst([escape(word), ...extraLatin])})\\s*[:：]?\\s*$`, 'i');
}

export function buildPallaviRegex(languageCode: string): RegExp {
  return buildOneWordRegex(sectionWordsForLanguage(languageCode).pallavi, ['pallavi']);
}

export function buildAnupallaviRegex(languageCode: string): RegExp {
  return buildOneWordRegex(sectionWordsForLanguage(languageCode).anupallavi, ['anupallavi']);
}

export function buildCaranamRegex(languageCode: string): RegExp {
  return buildOneWordRegex(sectionWordsForLanguage(languageCode).caranam, [
    'caraṇa[mṁ]?',
    'charanam',
    'caranam',
    'samasti\\s+caraṇa[mṁ]?',
  ]);
}
